import { Actions, Async, FSpread } from '../../helpers/types';
import {
  useGetInfluencerQuery,
  useUpsertOrDeleteExclusivePricesMutation,
} from '../../../../generated/graphql';
import { getVariablesFromPrice } from './helpers';
import { toCreator } from './mappers';
import toast from 'react-hot-toast';
import { useUserStore } from '@client/new/stores/user.store';

export type UseExclusiveModelParams = {
  influencerId?: string;
};

type SaveExclusive = {
  monthly?: string;
  annual?: string;
  callback?: () => void;
};

export type PayloadMap = {
  saveExclusive: SaveExclusive;
};

export type ExclusiveActions = Actions<PayloadMap>;
export type ExclusiveDispatcher = FSpread<PayloadMap>;

export type Creator = {
  id: number;
  isLive: boolean;
  isOnboarding: boolean;
  isExclusive: boolean;
  offering?: string[];
  subscriptionMonthlyPrice: number | null;
  subscriptionAnnualPrice: number | null;
};

export type ExclusiveModel = {
  submitting: boolean;
  creator: Creator;
  saveExclusive: (payload: SaveExclusive) => void;
};

export const useExclusiveModel = ({
  influencerId,
}: UseExclusiveModelParams): Async<ExclusiveModel> => {
  const { user } = useUserStore();
  const id = influencerId ? Number(influencerId) : user?.influencerId;
  const influencerQuery = useGetInfluencerQuery({
    variables: { id },
    fetchPolicy: 'network-only',
    skip: !id,
  });

  const [
    upsertOrDeleteExclusivePricesMutation,
    upsertOrDeleteExclusivePricesMutationState,
  ] = useUpsertOrDeleteExclusivePricesMutation();

  if (influencerQuery.loading) {
    return {
      type: 'fetching',
    };
  }

  if (influencerQuery.error || !influencerQuery.data?.creator) {
    return {
      type: 'fetch-error',
      value: 'Creator id is missing',
    };
  }

  const dispatch: ExclusiveDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as ExclusiveActions;
    switch (action.type) {
      case 'saveExclusive':
        saveExclusive(action.payload);
        return null;
    }
  };

  const saveExclusive = ({ monthly, annual, callback }: SaveExclusive) =>
    upsertOrDeleteExclusivePricesMutation({
      variables: {
        data: {
          influencerId: Number(id),
          ...getVariablesFromPrice({
            monthly,
            annual,
          }),
        },
      },
    })
      .then(() => {
        toast.success('Subscription price has been saved');
        typeof callback === 'function' && callback();
      })
      .catch((err) => {
        toast.error(err.message);
      });

  return {
    type: 'fetched',
    value: {
      saveExclusive: (payload: SaveExclusive) =>
        dispatch('saveExclusive', payload),
      submitting: upsertOrDeleteExclusivePricesMutationState.loading,
      creator: toCreator(influencerQuery.data.creator),
    },
  };
};
